// src/constants/enums.js
// Shared enums matching backend definitions (frogger/facility/enums.py)

export const GENDER = {
  MALE: 0,
  FEMALE: 1,
  UNSEXED: 2
};


/**
 * Convert numeric gender value to human-readable label
 * @param {number|string} v - Gender value (0, 1, or 2)
 * @returns {string} - "Male", "Female", or "Unknown"
 */
export const genderLabel = (v) => {
  const numericValue = Number(v);
  switch (numericValue) {
    case GENDER.MALE: return 'Male';
    case GENDER.FEMALE: return 'Female';
    case GENDER.UNSEXED: return 'Unknown';
    default: return 'Unknown';
  }
};

/**
 * Convert numeric gender value to single-letter abbreviation
 * @param {number|string} v - Gender value (0, 1, or 2)
 * @returns {string} - "M", "F", or "?"
 */
export const genderShort = (v) => {
  const numericValue = Number(v);
  switch (numericValue) {
    case GENDER.MALE: return 'M';
    case GENDER.FEMALE: return 'F';
    case GENDER.UNSEXED: return '?';
    default: return '?';
  }
};

/**
 * THE SEX OF ONE ANIMAL, as a WORD with its glyph beside it — never the
 * glyph alone (prod hotfix 2026-10-05, the operator on the racks drawer:
 * "if that is the gender, how am I supposed to tell"). `word` is
 * "Male" / "Female" / "Unsexed"; `glyph` is ♂ / ♀ or '' for unsexed (a
 * bare "?" read as a rendering fault, not as a sex); `label` is the two
 * together, "♂ Male", or just "Unsexed".
 *
 * "Unsexed", not `genderLabel`'s "Unknown": the animal's sex is not
 * unknown to the data, it has not been determined yet — the backend
 * enum's own word (facility/enums.py Gender.UNSEXED).
 */
/**
 * HOW THE SEX IS KNOWN (Q-sex-certainty, operator ruling 2026-10-14) —
 * `Animal.sex_basis`, the backend's `facility.enums.SexBasis`. `gender`
 * stays THE sex; the basis qualifies it. "Presumed" is the same level as
 * probable and is not stored separately (it reads "Probable"). Unsexed
 * iff the sex is Unsexed.
 */
export const SEX_BASIS = {
  CONFIRMED: 'confirmed',
  TEMPERATURE: 'temperature',
  PROBABLE: 'probable',
  UNSEXED: 'unsexed',
};

/** The qualifier beside the sex: "temp-sexed" / "probable", '' otherwise. */
export const sexBasisQualifier = (basis) => (
  { temperature: 'temp-sexed', probable: 'probable' }[basis] || '');

/**
 * The basis choices a sexed animal's editor offers. No "Unsexed": an
 * unsexed animal is set by its SEX, and the server refuses `unsexed` over
 * a male or female.
 */
export const SEX_BASIS_OPTIONS = [
  { value: SEX_BASIS.CONFIRMED, label: 'Confirmed' },
  { value: SEX_BASIS.TEMPERATURE, label: 'Temp-sexed' },
  { value: SEX_BASIS.PROBABLE, label: 'Probable (presumed)' },
];

/**
 * `sexDisplay(gender, basis)` — `basis` is optional. A temperature-sexed
 * or probable sex adds its qualifier to the LABEL ("♂ Male · temp-sexed",
 * "♀ Female · probable"); a confirmed one shows none ("♂ Male"); `word`
 * and `glyph` never change, so every reader that compares the sex keeps
 * comparing the sex. `qualifier` is the bare qualifier ('' when none).
 */
export const sexDisplay = (v, basis) => {
  const n = v === null || v === undefined || v === '' ? NaN : Number(v);
  const qualifier = sexBasisQualifier(basis);
  const tail = qualifier ? ` · ${qualifier}` : '';
  if (n === GENDER.MALE) {
    return { word: 'Male', glyph: '♂', label: `♂ Male${tail}`, qualifier };
  }
  if (n === GENDER.FEMALE) {
    return { word: 'Female', glyph: '♀', label: `♀ Female${tail}`, qualifier };
  }
  return { word: 'Unsexed', glyph: '', label: 'Unsexed', qualifier: '' };
};

/**
 * THE STOREFRONT'S WORDS (Q-sex-certainty): "Temp-sexed female",
 * "Probable male", plain "Female" / "Male" when confirmed, "Unsexed".
 * The twin of the backend's `services/sex_basis.listing_sex_label`.
 */
export const listingSexLabel = (gender, basis) => {
  const { word, qualifier } = sexDisplay(gender, basis);
  if (!qualifier || word === 'Unsexed') return word;
  const said = `${qualifier} ${word.toLowerCase()}`;
  return said.charAt(0).toUpperCase() + said.slice(1);
};

/**
 * The sex choices every select offers — the Add Animal form, the grid
 * row's Sex cell, the Animals filter. The words are `sexDisplay`'s own
 * ("Unsexed", never "Unknown" — Cowork 2026-10-23: the Add Animal form
 * said "Gender: Unknown" beside a grid that says "Sex · Unsexed"), so a
 * select and the cell at rest can never name the same value differently.
 * The field is called "Sex" (SEX_FIELD_LABEL) wherever it is labelled.
 */
export const SEX_FIELD_LABEL = 'Sex';

export const GENDER_OPTIONS = [GENDER.MALE, GENDER.FEMALE, GENDER.UNSEXED]
  .map((value) => ({ label: sexDisplay(value).word, value }));

/**
 * THE "SEXED PAIR" RULE (Q-sex-certainty review, BLOCK 10) — the twin of
 * the backend's `services/sex_basis.is_sexed_pair`. A pair is a SEXED pair
 * only when it is exactly one male and one female and BOTH sexes are
 * `confirmed`; a temp-sexed or probable member (or one whose basis did not
 * come on the wire) makes it a plain "Pair". Every lot badge asks this.
 */
export const isSexedPair = (members) => {
  const list = Array.isArray(members) ? members : [];
  if (list.length !== 2) return false;
  const genders = list.map((m) => Number(m?.gender)).sort();
  return genders[0] === GENDER.MALE && genders[1] === GENDER.FEMALE
    && list.every((m) => m?.sex_basis === SEX_BASIS.CONFIRMED);
};

/**
 * THE ROW'S SEX CELL SETS THE SEX AND HOW IT IS KNOWN IN ONE PATCH
 * (Q-sex-certainty review, item 14). A temperature that predicted MALE
 * does not make an animal a temp-sexed female, so the server refuses a
 * male<->female flip over a temp-sexed/probable basis that names no new
 * basis. The cell's choices are therefore the PAIRS — "Male",
 * "Male · temp-sexed", "Male · probable", the same for Female, and
 * "Unsexed" — and a choice sends `{gender, sex_basis}` together
 * (`{gender: 2}` alone for Unsexed). "Male" means a confirmed male.
 */
export const SEX_AND_BASIS_OPTIONS = [GENDER.MALE, GENDER.FEMALE]
  .flatMap((g) => SEX_BASIS_OPTIONS.map((b) => ({
    value: `${g}:${b.value}`,
    label: sexDisplay(g, b.value).label.replace(/^[♂♀] /, ''),
  })))
  .concat([{ value: String(GENDER.UNSEXED), label: 'Unsexed' }]);

/** The cell's value for a row: "0:temperature", "1:confirmed", "2". */
export const sexAndBasisValue = (gender, basis) => {
  const n = gender === null || gender === undefined || gender === ''
    ? NaN : Number(gender);
  if (n === GENDER.MALE || n === GENDER.FEMALE) {
    return `${n}:${basis && basis !== SEX_BASIS.UNSEXED ? basis : SEX_BASIS.CONFIRMED}`;
  }
  return String(GENDER.UNSEXED);
};

/** The PATCH body a cell value sends — numbers on the wire for `gender`. */
export const sexAndBasisPatch = (value) => {
  const [g, b] = String(value).split(':');
  const gender = Number(g);
  return (gender === GENDER.MALE || gender === GENDER.FEMALE) && b
    ? { gender, sex_basis: b }
    : { gender: GENDER.UNSEXED };
};

/**
 * "How it's sexed" in words, for a read-only place (the panel): the
 * option's own label ("Confirmed", "Temp-sexed", "Probable (presumed)"),
 * or "Unsexed" for an animal with no sex to qualify.
 */
export const sexBasisWords = (gender, basis) => {
  const n = gender === null || gender === undefined || gender === ''
    ? NaN : Number(gender);
  if (n !== GENDER.MALE && n !== GENDER.FEMALE) return 'Unsexed';
  const found = SEX_BASIS_OPTIONS.find((o) => o.value === basis);
  return found ? found.label : 'Confirmed';
};
